import { Box } from "@mui/material";
import * as Sentry from "@sentry/react";
import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "src/contexts/AuthContext";
import { LoginCodeChannel } from "src/features/users/services/requestLoginCode";
import { isCodeSignInAvailable } from "src/firebase";
import getFrom from "src/shared/utils/generic/getFrom";
import CodeStep from "./CodeStep";
import IdentifierStep from "./IdentifierStep";
import { getLoginHandoff } from "./loginHandoffState";
import PasswordStep from "./PasswordStep";

type LoginStep = "identifier" | "code" | "password";

/**
 * Student and admin login. Leads with a single email-or-phone field that sends
 * a one-time sign-in code (SMS or email to match what was typed), then a code
 * entry step. Password sign-in remains available to non-admin accounts from
 * the first step, while Identity Platform rejects admin password credentials.
 */
export default function LoginScreen(): JSX.Element {
  const location = useLocation();
  const handoff = getLoginHandoff(location.state);
  const [step, setStep] = useState<LoginStep>(() =>
    isCodeSignInAvailable
      ? handoff?.codeSent
        ? "code"
        : "identifier"
      : "password"
  );
  const [identifier, setIdentifier] = useState(() => handoff?.identifier ?? "");
  const [channel, setChannel] = useState<LoginCodeChannel>(() =>
    handoff?.codeSent ? handoff.channel : "email"
  );
  const authCtx = useAuth();
  const navigate = useNavigate();
  const from = getFrom(location) || "/";

  // Reported from a component rather than at module scope: Sentry.init() runs in
  // App.tsx, so a module-level capture in src/firebase could fire before it.
  useEffect(() => {
    if (isCodeSignInAvailable) return;
    Sentry.captureMessage(
      "REACT_APP_FIREBASE_APP_CHECK_SITE_KEY is missing, so requestLoginCode/verifyLoginCode " +
        "would reject every call as unauthenticated. Only non-admin accounts retain password fallback.",
      "error"
    );
  }, []);

  if (authCtx?.session) {
    return <Navigate to="/" />;
  }

  const handleSignedIn = () => navigate(from, { replace: true });

  return (
    <Box>
      {step === "identifier" && (
        <IdentifierStep
          identifier={identifier}
          onIdentifierChange={setIdentifier}
          onCodeSent={(sentChannel) => {
            setChannel(sentChannel);
            setStep("code");
          }}
          onUsePassword={() => setStep("password")}
        />
      )}
      {step === "code" && (
        <CodeStep
          identifier={identifier}
          channel={channel}
          onSignedIn={handleSignedIn}
          onUseDifferentIdentifier={() => setStep("identifier")}
        />
      )}
      {step === "password" && (
        <PasswordStep
          initialEmail={identifier.includes("@") ? identifier.trim() : ""}
          onSignedIn={handleSignedIn}
          onUseCode={
            isCodeSignInAvailable
              ? (passwordEmail) => {
                  setIdentifier(passwordEmail.trim());
                  setStep("identifier");
                }
              : undefined
          }
        />
      )}
    </Box>
  );
}
